<template>
    <div class="meeting-create" data-test-view="meeting-create">
        <div class="row">
            <div class="col-md-12 text-center">
                <!-- Headline -->
                <div class="text-center" style="margin-top: 25px">
                    <img :src="iconMeetings" class="placeholder-list" alt="Icon Meetings" />
                </div>

                <l-space :size="1" />

                <p>{{ $t("meetings.helpPurpose") }}</p>
                <!--/Headline -->

                <hr />
            </div>
        </div>
        <div class="row">
            <div class="col-md-12">
                <div v-show="!isGoogleCalendarSyncEnabled">
                    <button class="btn btn-default btn-raised" @click="authGoogleCalendar">
                        <i class="fa fa-google"></i>
                        {{ $t("profile.enableGoogleCalendarIntegration") }}
                    </button>
                </div>
                <l-google-calendar-event-select
                    v-show="isGoogleCalendarSyncEnabled && !isLinkedToGoogle"
                    :on-select="googleEventLink"
                    :on-error="googleEventLinkError"
                />
                <button v-show="isLinkedToGoogle" class="btn" @click="googleEventUnlink">
                    {{ $t("meetings.unlinkGoogleCalendarEvent") }}
                </button>

                <l-space :size="3" />
            </div>
        </div>
        <div class="row">
            <div class="col-md-12">
                <!-- Title -->
                <l-input-text
                    v-model="meeting.title"
                    type="text"
                    optional
                    hide-help-block
                    :label="$t('meetings.labelTitle')"
                    :placeholder="$t(`meetings.labelTitle`)"
                    data-test-id="meeting-create-title"
                    :readonly="isLinkedToGoogle"
                />
                <!--/Title -->

                <l-space :size="1" />

                <!-- Participants -->
                <l-input-typeahead
                    :label="$t('meetings.labelParticipants')"
                    :placeholder="$t(`meetings.placeholderAdd`)"
                    :data="participants"
                    :on-select="typeaheadParticipantsOnSelect"
                    :on-input-change="typeaheadParticipantsOnInputChange"
                    data-test-id="meeting-create-participant"
                />
                <div v-if="meeting.otherUsers.length">
                    <!-- Participants from Google Calendar Meeting - non editable -->
                    <span v-for="u in googleCalendarUsers" :key="u._id" class="r-default-margin-s">
                        <span class="label label-primary" tabindex="0">
                            {{ u.firstname }} {{ u.lastname }}
                        </span>
                    </span>
                    <span v-for="u in localUsers" :key="u._id" class="r-default-margin-s">
                        <span
                            class="label label-primary clickable"
                            tabindex="0"
                            @click="() => removeSelectedParticipant(u._id)"
                            @keyup.enter="() => removeSelectedParticipant(u._id)"
                        >
                            {{ u.firstname }} {{ u.lastname }}
                            <i class="fa fa-remove"></i>
                        </span>
                    </span>
                    <l-space :size="1" />
                </div>
                <!--/Participants -->

                <!-- ExistingMeetings -->
                <div class="existing-meetings">
                    <label v-show="existingList && existingList.length">
                        {{ $t("meetings.labelExistingMeetings") }}
                    </label>
                    <button
                        v-for="item in existingList"
                        :key="item._id"
                        data-test-class="existing-meeting"
                        class="btn btn-default btn-sm btn-rounded-default color-primary"
                        style="margin-right: 10px"
                        @click="() => goToMeetingDetails(item._id)"
                    >
                        <span>
                            <!-- FIXME might need a better date format here -->
                            {{ item.cycle.nextTime | moment("MMM DD, YYYY") }}
                        </span>
                        <span>{{ item.title }} </span>
                    </button>
                    <l-space :size="1" />
                </div>
                <!-- /ExistingMeetings -->

                <l-space :size="1" />

                <!-- Date -->
                <div class="form-group label-floating">
                    <div class="row">
                        <div v-if="!meeting.gCalEvent" class="col-md-3">
                            <l-select
                                v-model="meeting.cycle.frequency"
                                :label="$t('meetings.labelFrequency')"
                                :options="optionsFrequency"
                                :value="meeting.cycle.frequency"
                                data-test-id="meeting-frequency"
                            />
                        </div>
                        <div class="col-md-3">
                            <l-date-picker
                                :label="$t(`meetings.labelFirstTime`)"
                                :placeholder="''"
                                :value="new Date(meeting.cycle.nextTime)"
                                :on-date-change="updateNextDate"
                                :readonly="isLinkedToGoogle"
                                data-test-id="meeting-nextTime-date"
                            />
                        </div>
                        <div class="col-md-4">
                            <l-time-picker
                                :value="new Date(meeting.cycle.nextTime)"
                                :use-meridian-time="useMeridianTime"
                                :readonly="isLinkedToGoogle"
                                data-test-id="meeting-nextTime-time"
                                @change="updateNextTime"
                            />
                        </div>
                        <div class="col-md-2">
                            <l-input-text
                                ref="input-title"
                                v-model="meeting.cycle.duration"
                                type="text"
                                :readonly="isLinkedToGoogle"
                                :label="$t(`meetings.labelDuration`)"
                                :placeholder="$t(`meetings.labelDuration`)"
                                data-test-id="meeting-duration"
                            />
                        </div>
                    </div>

                    <div
                        v-show="!this.meeting.gCalEvent && isGoogleCalendarSyncEnabled"
                        class="row"
                    >
                        <div class="col-md-12">
                            <label>
                                <input v-model="meeting.createGCalMeeting" type="checkbox" />{{
                                    $t("meetings.createGoogleCalendarMeeting")
                                }}
                            </label>
                        </div>
                    </div>
                </div>
                <!--/Date -->

                <div class="form-group label-floating">
                    <l-template-typeahead :on-select="selectTemplate" />
                    <div
                        v-if="template"
                        class="tw-flex tw-flex-row tw-justify-between tw-items-center"
                    >
                        <div>Template: {{ template }}</div>
                        <button class="btn" @click="removeTemplate">
                            {{ $t(`meetings.btnRemoveTemplate`) }}
                        </button>
                    </div>
                </div>

                <l-space :size="1" />

                <!-- Error -->
                <l-text-alert
                    v-if="error"
                    type="danger"
                    :text="$t(`meetings.helpAddParticipants`)"
                />

                <l-text-alert
                    v-if="googleLinkError"
                    type="danger"
                    :text="$t(`meetings.noLinkableEventsFound`)"
                />
                <!--/Error -->

                <!-- Create Button -->
                <button
                    class="btn btn-primary btn-raised btn-block"
                    data-test-id="meeting-create-btn"
                    @click="create"
                >
                    <i v-if="updating" class="fa fa-refresh fa-spin"></i>
                    <span>{{ $t("meetings.btnCreateMeeting") }}</span>
                </button>
                <!--/Create Button -->
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import format from "@/utils/format-typeahead";

import LDatePicker from "@/components/LDatePicker";
import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LSelect from "@/components/LSelect";
import LSpace from "@/components/LSpace";
import LTextAlert from "@/components/LTextAlert";
import LTimePicker from "@/components/LTimePicker";
import LGoogleCalendarEventSelect from "@/components/LGoogleCalendarEventSelect";
import LTemplateTypeahead from "@/components/LMeetingTemplates/LTemplateTypeahead.vue";

import icon from "@/img/icons/settings.png";
import iconMeetings from "@/img/icons/meetings.png";

const GOOGLE_AUTH_REDIRECT_KEY = "source";
const GOOGLE_AUTH_REDIRECT_VALUE = "googleAuth";
export default {
    components: {
        LDatePicker,
        LInputText,
        LInputTypeahead,
        LSelect,
        LSpace,
        LTextAlert,
        LTimePicker,
        LGoogleCalendarEventSelect,
        LTemplateTypeahead,
    },
    data() {
        return {
            newCategoryName: "",
            showDeleteModal: false,
            showEditModal: false,
            currentFocus: "",
            currentSkill: {},
            meeting: {
                createGCalMeeting: false,
                gCalEvent: null,
                title: "",
                participants: [],
                otherUsers: [],
                cycle: {
                    nextTime: new Date(),
                    frequency: "every week",
                    duration: "15",
                    state: "enabled",
                    tz: Intl?.DateTimeFormat?.().resolvedOptions?.()?.timeZone,
                },
            },
            updating: false,
            error: false,
            googleLinkError: false,
            template: "",
        };
    },
    computed: {
        ...mapGetters({
            usersSearchList: "users/search/list",
            getUserList: "users/list/list",
            user: "users/current/user",
            teamSettings: "users/current/teamSettings",
            isGoogleCalendarSyncEnabled: "users/current/isGoogleCalendarSyncEnabled",
            createdMeetingId: "meetings/meetings/latestCreatedId",
            meetings: "meetings/meetings/listSortedByDate",
        }),
        participants() {
            let list = Array.from(this.usersSearchList);

            if (!list.length) {
                list = this.getUserList({ key: "directTeam" });
            }

            return (
                list
                    // Filter current user
                    .filter(user => user._id !== this.user._id)
                    // Filter users that have already been selected
                    .filter(
                        user =>
                            this.meeting.otherUsers &&
                            !this.meeting.otherUsers.find(u => u._id === user._id),
                    )
                    // Format list to fit typeahead data requirements
                    .map(format.users)
            );
        },
        optionsFrequency() {
            return [
                { value: "manual", text: this.$t("meetings.optionManual") },
                { value: "daily", text: this.$t("meetings.optionDaily") },
                { value: "every week", text: this.$t("meetings.optionWeekly") },
                { value: "every 2 weeks", text: this.$t("meetings.optionBiweekly") },
                { value: "every month", text: this.$t("meetings.optionMonthly") },
                { value: "every 2 months", text: this.$t("meetings.option2Months") },
                { value: "every 3 months", text: this.$t("meetings.option3Months") },
                { value: "every 4 months", text: this.$t("meetings.option4Months") },
                { value: "every 6 months", text: this.$t("meetings.option6Months") },
            ];
        },
        existingList() {
            if (!this.meeting.participants || !this.meeting.participants.length) {
                return [];
            }
            const participantIds = [
                ...this.meeting.participants.map(p => p._id),
                this.user._id,
            ].sort();

            return this.meetings.filter(
                m =>
                    JSON.stringify(m.participants.map(p => p._id).sort()) ===
                    JSON.stringify(participantIds),
            );
        },
        googleCalendarUsers() {
            return this.meeting.otherUsers.filter(user => user.source === "googleCalendar");
        },
        localUsers() {
            return this.meeting.otherUsers.filter(user => user.source === "local");
        },
        isLinkedToGoogle() {
            return Boolean(this.meeting.gCalEvent);
        },
        useMeridianTime() {
            return this.teamSettings.useMeridianTime;
        },
    },
    created() {
        this.icon = icon;
        this.iconMeetings = iconMeetings;

        if (this.$route.query[GOOGLE_AUTH_REDIRECT_KEY] === GOOGLE_AUTH_REDIRECT_VALUE) {
            this.$store.dispatch("auth/performSessionRefresh");
        }
    },
    methods: {
        async authGoogleCalendar() {
            this.$store.dispatch("googleCalendar/calendar/startAuth", {
                redirect: `/app/#/meetings/create?${GOOGLE_AUTH_REDIRECT_KEY}=${GOOGLE_AUTH_REDIRECT_VALUE}`,
            });
        },
        goToMeetingDetails(id) {
            this.$router.push(`/meetings/details/${id}`);
        },
        typeaheadParticipantsOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },
        selectTemplate(template) {
            this.template = template.title;
            this.meeting.agendaQuestions = template.sections.map(section => section._id);
            this.meeting.agendaQuestionsCustom = true;
        },
        removeTemplate() {
            this.template = null;
            delete this.meeting.agendaQuestions;
            this.meeting.agendaQuestionsCustom = false;
        },
        typeaheadParticipantsOnSelect(selected) {
            if (!this.meeting.participants.find(u => u._id === selected._id)) {
                this.meeting.participants.push(selected);
                this.meeting.otherUsers.push({ ...selected, source: "local" });
            }
        },
        removeSelectedParticipant(_id) {
            this.meeting.participants = this.meeting.participants.filter(u => u._id !== _id);
            this.meeting.otherUsers = this.meeting.otherUsers.filter(u => u._id !== _id);
        },
        openGCalEvent() {
            window.open(this.meeting.gCalEvent.event.htmlLink, "_blank", "noreferrer noopener");
        },
        googleEventLink(gCalEvent) {
            this.googleLinkError = false;
            this.meeting.gCalEvent = gCalEvent;
            this.meeting.gCalAction = "link";
            this.meeting.title = gCalEvent.summary;
            this.updateNextDate(
                this.$moment(gCalEvent.start.dateTime ?? gCalEvent.start.date).toDate(),
            );
            this.updateNextTime(
                this.$moment(gCalEvent.start.dateTime ?? gCalEvent.start.date).valueOf(),
            );
            this.meeting.cycle.duration = this.$moment(gCalEvent.end.dateTime ?? gCalEvent.end.date)
                .diff(gCalEvent.start.dateTime ?? gCalEvent.start.date, "minutes")
                .toString();
            const emails = (gCalEvent.attendees || [])
                .map(attendee => attendee.email)
                .filter(email => email != this.user.username);
            this.$store.dispatch("users/search/findByEmailBatch", { emails }).then(users => {
                this.meeting.otherUsers = users.map(user => ({
                    ...user,
                    source: "googleCalendar",
                }));
                this.meeting.participants = [this.user, ...users];
            });
        },
        googleEventLinkError() {
            this.googleLinkError = true;
        },
        googleEventUnlink() {
            this.meeting.gCalEvent = null;
            this.meeting.otherUsers = this.meeting.otherUsers.map(u => ({ ...u, source: "local" }));
        },
        updateNextDate(x) {
            if (this.$moment(this.meeting.cycle.nextTime).isValid() && this.$moment(x).isValid()) {
                this.meeting.cycle.nextTime = this.$moment(this.meeting.cycle.nextTime)
                    .set("year", x.getFullYear())
                    .set("month", x.getMonth())
                    .set("date", x.getDate());
            }
        },
        updateNextTime(time) {
            if (this.$moment(time).isValid()) {
                this.meeting.cycle.nextTime = new Date(time);
            }
        },

        async create() {
            this.updating = true;
            this.error = false;

            if (!this.meeting.otherUsers || !this.meeting.otherUsers.length) {
                this.error = true;
                this.updating = false;
                return;
            }

            const participants = [
                ...new Set([...this.meeting.participants.map(u => u._id), this.user._id]),
            ]; // always include user itself

            await this.$store.dispatch({
                type: "meetings/meetings/create",
                payload: {
                    data: {
                        ...this.meeting,
                        participants,
                    },
                },
            });

            this.$router.push(`/meetings/details/${this.createdMeetingId}`);
        },
    },
};
</script>

<style scoped>
.existing-meetings label {
    display: block;
    font-size: 12px;
    color: #9e9e9e;
    margin: 0;
}
</style>
